<template lang="pug">
v-dialog(v-model="show" max-width="1000" persistent :fullscreen="$vuetify.breakpoint.mdAndDown")
    v-card.d-flex.flex-column(flat)
        v-card-title.headline {{ $t('optInDialog.dialogTitle') }}
        v-card-text.pb-1.flex-shrink-0.flex-grow-1.card-body(ref="cardBody" :style="{height: cardBodyHeight}")
            v-row
                v-col(cols="12" sm="12" md="6" lg="6" xl="6")
                    v-card(flat)
                        v-card-text.pt-0.text-center {{ $t('optInDialog.datePickerHeading') }}
                        v-card-text.d-flex.justify-center.align-center.fill-height.pa-0.ma-0
                            v-date-picker(
                                v-model="dateSelection" multiple scrollable next-icon="fas fa-chevron-right" prev-icon="fas fa-chevron-left"
                                first-day-of-week="1" show-week no-title color="primary"
                                :full-width="$vuetify.breakpoint.smAndDown"
                                @change="onDateSelectionChange"
                            )
                v-col.mt-4.mt-md-0.mt-lg-0.mt-xl-0.pa-0.pa-md-3(cols="12" sm="12" md="6" lg="6" xl="6")
                    v-card(flat)
                        v-card-text.pt-0.text-center(ref="listLabelHeader") {{ $t('optInDialog.boarderListHeading') }}
                        v-card-text.pa-0.boarder-list
                            v-list(:max-height="listMaxHeight")
                                v-list-item(v-for="(boarder, idx) in currentBoarderList" :key="idx" @click="")
                                    v-list-item-action
                                        v-checkbox(v-model="boarder.isSelected" on-icon="fas fa-check-square" off-icon="fal fa-square")
                                    v-list-item-content.flex-shrink-0.flex-md-grow-1 {{ formatBoarderName(boarder) }}
                                    v-list-item-action
                                        template(v-if="isVerificationPending")
                                            v-icon far fa-circle-notch fa-spin
                                        template(v-else)
                                            v-icon(v-if="boarder.issues && boarder.issues.length > 0" color="warning") far fa-exclamation-circle
                                            v-icon(v-if="boarder.issues && boarder.issues.length === 0" color="green") far fa-check-circle

                v-alert.ma-0.alert-box(
                    v-model="showWarning"
                    dismissible type="warning" icon="fas fa-info-circle"
                    :style="warningStyle" :dense="$vuetify.breakpoint.smAndDown"
                )
                    span(v-html="$t('optInDialog.alertMessage')")
                    template(v-slot:close="{toggle}")
                        v-btn(icon x-small @click="toggle")
                            v-icon fas fa-times-circle

        v-divider
        v-card-actions
            v-spacer
            v-btn(color="primary" @click="onSubmitButtonClick" :disabled="isSubmitButtonDisabled" :loading="isSaveInProgress") {{ submitButtonLabel }}
            v-btn(text @click="onCancelButtonClick") {{ $t('optInDialog.cancelButtonLabel') }}

</template>

<!--suppress JSMismatchedCollectionQueryUpdate -->
<script lang="ts">
import {Component, Prop, Vue, Watch} from 'vue-property-decorator'
import {Action} from 'vuex-class'
import _ from 'lodash'
import {BoarderResponse, OptInVerificationResponseItem} from '@/types/service-response'
import {
  BatchReservationOptInPayload,
  BoarderOptInPayload,
  OptInVerificationPayload
} from '@/types/payload'
import moment from 'moment'
import {
  FETCH_OPT_IN_VERIFICATION,
  PERFORM_BATCH_RESERVATION_OPT_IN,
} from '@/store/action-types'
import {BoarderOptInIssue} from '@/types/enums'

@Component({})
export default class OptInDialog extends Vue {
  @Prop({type: Boolean, default: false}) readonly show!: boolean
  @Prop({type: Array, required: true}) readonly boarderList!: BoarderResponse[]

  $refs!: {
    cardBody: HTMLElement,
    listLabelHeader: HTMLElement
  }

  @Action(FETCH_OPT_IN_VERIFICATION) fetchOptInVerification!: { (payload: OptInVerificationPayload): Promise<OptInVerificationResponseItem[]> }
  @Action(PERFORM_BATCH_RESERVATION_OPT_IN) performReservationOptIn!: { (payload: BatchReservationOptInPayload): Promise<void> }

  private currentBoarderList: BoarderOptInPayload[] = []
  private dateSelection: string[] = []
  private isVerificationPending: boolean = false
  private listMaxHeight: number | undefined = 0
  private isSaveInProgress: boolean = false

  get submitButtonLabel(): string {
    const count = this.currentBoarderList.filter((b: BoarderOptInPayload) => b.isSelected).length

    return this.$tc('optInDialog.submitButtonLabel', count, {count})
  }

  get cardBodyHeight(): string {
    if (this.$vuetify.breakpoint.mdAndDown) {
      return `0px`
    }

    return `450px`
  }

  get boardersForOptIn(): BoarderOptInPayload[] {
    return this.currentBoarderList.filter((b) => b.isSelected)
  }

  get isSubmitButtonDisabled(): boolean {
    return this.dateSelection.length === 0 || this.boardersForOptIn.length === 0 || this.isVerificationPending
  }

  get showWarning(): boolean {
    return this.currentBoarderList.some((b) => b.issues && b.issues.length > 0)
  }

  set showWarning(value: boolean) {
  }

  get warningStyle(): Object | undefined {
    if (this.$vuetify.breakpoint.mdAndDown) {
      return {
        position: 'fixed',
        bottom: `64px`
      }
    } else {
      return {
        position: 'absolute',
        bottom: `12px`
      }
    }
  }

  @Watch('dateSelection', {immediate: true})
  onDateSelectionChange(newVal: string[]) {
    if (newVal.length) {
      this.isVerificationPending = true

      this.verifyDateSelection()
    } else {
      this.currentBoarderList.forEach((b) => b.issues = undefined)
    }
  }

  formatBoarderName(boarder: BoarderOptInPayload): string {
    return `${boarder.boarder.person.firstName} ${boarder.boarder.person.familyName}`
  }

  calculateListMaxHeight(): number | undefined {
    if (this.$vuetify.breakpoint.smAndDown) {
      return undefined
    } else if (this.$vuetify.breakpoint.mdOnly) {
      if (this.$refs.cardBody) {
        const mainHeight = this.$refs.cardBody.clientHeight
        const headerHeight = this.$refs.listLabelHeader.clientHeight

        return mainHeight - headerHeight - 28
      }
    }

    return 368
  }

  onCancelButtonClick(): void {
    this.$emit('close')
  }

  onOrientationChange(): void {
    setTimeout(() => this.listMaxHeight = this.calculateListMaxHeight(), 3000)
  }

  async onSubmitButtonClick(): Promise<void> {
    const boarderIdList: number[] = this.boardersForOptIn.map((b) => b.boarder.id)
    const dateList: Date[] = this.dateSelection.map((d) => moment(d).toDate())

    try {
      this.isSaveInProgress = true

      await this.performReservationOptIn({
        boarderIdList,
        dateList
      })

      this.$nextTick(() => this.$emit('close'))
    } catch (e) {
      // TODO: handle error in some generic way
    } finally {
      this.isSaveInProgress = false
    }
  }

  async verifyDateSelection(): Promise<void> {
    const payload: OptInVerificationPayload = {
      boarderIdList: this.currentBoarderList.map((b) => b.boarder.id),
      dateList: this.dateSelection.map((d: string) => moment(d).toDate())
    }

    const verificationResponse: OptInVerificationResponseItem[] = await this.fetchOptInVerification(payload)

    this.currentBoarderList.forEach((currentBoarder: BoarderOptInPayload) => {
      const boarder = verificationResponse.find((b) => b.boarder.id === currentBoarder.boarder.id)

      if (boarder && boarder.canceledOfferReservations.length > 0) {
        currentBoarder.issues = [BoarderOptInIssue.HasCanceledReservation]
      } else {
        currentBoarder.issues = []
      }
    })

    this.isVerificationPending = false
  }

  created(): void {
    screen.orientation.addEventListener('change', this.onOrientationChange)

    this.verifyDateSelection = _.debounce(this.verifyDateSelection, 500)
  }

  mounted(): void {
    this.currentBoarderList = _.cloneDeep(this.boarderList)
      .sort((a, b) => a.person.familyName.localeCompare(b.person.familyName))
      .map((b: BoarderResponse): BoarderOptInPayload => {
        return {
          boarder: b,
          isSelected: true,
          issues: undefined
        }
      })

    this.$nextTick(() => this.listMaxHeight = this.calculateListMaxHeight())
  }

  beforeDestroy() {
    screen.orientation.removeEventListener('change', this.onOrientationChange)
  }
}

</script>

<style lang="stylus" scoped>
.boarder-list
  overflow auto

.issue-cell
  color #fb8c00

.alert-box
  right 24px
  left 24px

.card-body
  overflow auto
  position relative

>>> .v-stepper__wrapper
  overflow auto !important

>>> .v-stepper__step--editable
  .v-icon
    font-size small !important
</style>
